/**
 * `/settings` — notification toggles (spec §2.8), replay the intro (spec
 * §2.5), sign out, delete account (spec §3) and data requests.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { NotificationPrefs } from '@drawearth/shared';
import { Icon, ThemeChips, TopBar } from '../components/Widgets.tsx';
import { api } from '../lib/api.ts';
import { deleteAccount, refreshUser, signOut } from '../lib/session.ts';
import { toast, useApp } from '../lib/store.ts';

const PREF_LABELS: Array<[keyof NotificationPrefs, string, string]> = [
  ['push', 'Push notifications', 'On your phone, even when the app is closed'],
  ['inApp', 'In-app activity', 'The bell in the top bar'],
  ['overwrite', 'Painted over', 'When a chunk of your pixels gets covered'],
  ['collab', 'Neighbours', 'When someone paints right next to your work'],
  ['badge', 'Badges', 'When you earn one'],
  ['moderation', 'Moderation notices', 'If a moderator removes your paint'],
];

export function Settings() {
  const user = useApp((s) => s.user);
  const authReady = useApp((s) => s.authReady);
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(user?.notificationPrefs ?? null);
  const [live, setLiveState] = useState(!!user?.liveUntil && user.liveUntil > Date.now());
  useEffect(() => {
    if (user) {
      setPrefs(user.notificationPrefs);
      setLiveState(!!user.liveUntil && user.liveUntil > Date.now());
    }
  }, [user]);
  if (!authReady) return <div className="page page--paper" />;
  if (!user || !prefs) return <Navigate to="/signin?next=/settings" replace />;
  const setPref = async (k: keyof NotificationPrefs, v: boolean): Promise<void> => {
    setPrefs({ ...prefs, [k]: v });
    try {
      await api.setNotificationPrefs({ [k]: v });
      await refreshUser();
    } catch {
      toast('That preference did not save. Try again.');
      setPrefs(user.notificationPrefs);
    }
  };
  return (
    <div className="page page--paper">
      <TopBar />
      <div className="sheet">
        <h1>Settings</h1>
        <section className="card">
          <h2>Theme</h2>
          <p className="muted">The canvas is black at night and white in the light. Tools stay paper either way.</p>
          <ThemeChips />
        </section>
        <section className="card">
          <h2>Tell me when…</h2>
          {PREF_LABELS.map(([k, label, hint]) => (
            <label key={k} className="toggle">
              <span className="toggle__text">
                {label}
                <br />
                <span className="muted" style={{ fontWeight: 500, fontSize: 13 }}>
                  {hint}
                </span>
              </span>
              <input type="checkbox" checked={prefs[k]} onChange={(e) => void setPref(k, e.target.checked)} data-testid={`pref-${k}`} />
              <span className="toggle__track" />
            </label>
          ))}
        </section>
        <section className="card">
          <h2>Go live</h2>
          <p className="muted">Off by default. When you go live, your avatar and handle appear as a small callout where you are painting, for four hours or until you switch it off. Never in playback, never on the globe.</p>
          <label className="toggle">
            <span className="toggle__text">Show me where I paint</span>
            <input
              type="checkbox"
              checked={live}
              onChange={async (e) => {
                const v = e.target.checked;
                setLiveState(v);
                try {
                  await api.setLive(v);
                  await refreshUser();
                } catch {
                  setLiveState(!v);
                  toast('Could not change that right now.');
                }
              }}
              data-testid="pref-live"
            />
            <span className="toggle__track" />
          </label>
        </section>
        <section className="card">
          <h2>Intro</h2>
          <p className="muted">Watch the globe find you again.</p>
          <div>
            <button type="button" className="btn btn--paper" onClick={() => navigate('/?intro=1')}>
              <Icon name="globe" /> Replay the intro
            </button>
          </div>
        </section>
        <section className="card">
          <h2>Your data</h2>
          <p className="muted">
            We keep your handle, avatar, badges, ink, push tokens and a few totals. Never your email, never your coordinates. Read the <Link to="/privacy">privacy policy</Link>.
          </p>
          <div>
            <a className="btn btn--paper" href="mailto:privacy@drawearth.app?subject=Data%20request">
              Request a copy of my data
            </a>
          </div>
        </section>
        <section className="card">
          <h2>Account</h2>
          <div>
            <button type="button" className="btn btn--paper" onClick={() => void signOut().then(() => navigate('/'))} data-testid="signout">
              Sign out
            </button>
          </div>
          <details className="danger">
            <summary>Delete account</summary>
            <p className="muted">This removes your handle, avatar, badges, tokens and stats, and unlinks your sign-in. Pixels you painted stay on the canvas, unattributed, so everyone's shared history stays whole.</p>
            <label>
              Type <code>delete</code> to confirm
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm" />
            </label>
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn btn--danger"
                disabled={confirm !== 'delete'}
                data-testid="delete-account"
                onClick={async () => {
                  try {
                    await deleteAccount();
                    toast('Your account is deleted.');
                    navigate('/');
                  } catch {
                    toast('Deletion did not go through. Try again.');
                  }
                }}
              >
                Delete my account
              </button>
            </div>
          </details>
        </section>
      </div>
    </div>
  );
}
